import {
    ClockCircleOutlined,
    ContactsOutlined,
    DesktopOutlined,
    FileOutlined,
    LogoutOutlined,
    MailOutlined,
    StarOutlined,
    SyncOutlined,
    TeamOutlined,
    TrophyOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { Button, Layout, Menu, Modal, Space, Tag, theme, Tooltip } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/DataContext';
import AdminContent from './AdminContent';
import ContactFormSubmitted from './ContactFormSubmitted.js';
import JuryDeadlineSettings from './JuryDeadlineSettings';
import OrchestraAssignments from './OrchestraAssignments';
import OrchestraSettings from './OrchestraSettings';
import PastRegistrantDashboard from './PastRegistrantDashboard';
import PerformerSessionsSettings from './PerformerSessionsSettings';
import PublicCustomersList from './PublicCustomersList';
import RegistrantDashboard from './RegistrantDashboard';
import ScoringRecap from './ScoringRecap';
import JuryManagement from './JuryManagement';
import SeatingEvent from './SeatEvent.js';
import SeatOccupancy from './SeatOccupancy';
import SystemSettings from './SystemSettings';
import TicketPricingSettings from './TicketPricingSettings';
import VenueSettings from './VenueSettings';
import VideoPenaltySettings from './VideoPenaltySettings';

const MENU_TITLES = {
    '1': 'Contact Form Submitted',
    '2': 'Admin Page',
    '3': 'Registrant Data',
    '5': 'Seating Event',
    '13': 'Past Registrants',
    '14': 'System Settings',
    '15': 'Orchestra Settings',
    '16': 'Public Customers',
    '17': 'Venue Settings',
    '18': 'Ticket Pricing Settings',
    '19': 'Performer Sessions Settings',
    '20': 'Jury Deadlines',
    '21': 'Scoring Recap',
    '22': 'Seat Occupancy',
    '23': 'Masterclass Settings',
    '24': 'Jury Management',
    '25': 'Orchestra Assignments',
    '27': 'Video Penalty Settings',
};

const { Header, Footer, Sider } = Layout;
function getItem(label, key, icon, onClick, children) {
    return {
        label,
        key,
        icon,
        onClick,
        children
    };
}

const AdminDashboard = () => {
    const { t } = useTranslation();
    const {
        signOut,
        loggedInUser,
        sessionRemainingMs,
        extendSession,
        SESSION_WARNING_MS,
    } = useAuth();
    const [collapsed, setCollapsed] = useState(false);
    const { token: { colorBgContainer }, } = theme.useToken();

    const [selectedKey, setSelectedKey] = useState('1'); // Initialize with default selected key

    const handleSignOut = () => {
        signOut();
    };

    const formatRemaining = (ms) => {
        if (!ms || ms <= 0) return '0m';
        const totalMinutes = Math.floor(ms / 60000);
        const seconds = Math.floor((ms % 60000) / 1000);
        if (totalMinutes > 0) {
            return `${totalMinutes}m ${seconds}s`;
        }
        return `${seconds}s`;
    };

    const isWarningOpen = Boolean(
        loggedInUser &&
        sessionRemainingMs !== undefined &&
        sessionRemainingMs > 0 &&
        sessionRemainingMs <= (SESSION_WARNING_MS || 180000)
    );

    const onMenuClick = (e) => {
        setSelectedKey(e.key); // Update selected key
    };

    const items = [
        getItem('Contact Form Submitted', '1', <ContactsOutlined />),
        getItem('Registrant Data', '3', <DesktopOutlined />),
        getItem('Past Registrants', '13', <MailOutlined />),
        // getItem('Old Seating Generator', '5', <MailOutlined />),
        getItem('Ticketing System', 'subTicketing', <DesktopOutlined />, () => { }, [
            getItem('System Settings', '14'),
            getItem('Venue Settings', '17'),
            getItem('Ticket Settings', '18'),
            getItem('Admin Page', '2'),
            getItem('Performer Sessions', '19'),
            getItem('Seat Occupancy', '22'),
            getItem('Public Customers', '16', <TeamOutlined />),
            getItem('Orchestra Settings', '15'),
            getItem('Orchestra Assignments', '25'),
        ]),
        getItem('Scoring Management', 'subJury', <TrophyOutlined />, () => { }, [
            getItem('Scoring Recap', '21', <StarOutlined />),
            getItem('Jury Deadlines', '20', <ClockCircleOutlined />),
            getItem('Jury Management', '24', <TeamOutlined />),
            getItem(t('videoPenalty.menuTitle'), '27', <ClockCircleOutlined />),
        ]),
        // getItem('User', 'sub1', <UserOutlined />, () => { }, [
        //     getItem('Tom', '7'),
        //     getItem('Bill', '8'),
        //     getItem('Alex', '9'),
        // ]),
        // getItem('Team', 'sub2', <TeamOutlined />, () => { }, [getItem('Team 1', '10'), getItem('Team 2', '11')]),
        getItem('Sign out', '12', <FileOutlined />, handleSignOut),
    ];


    const ShowWhichContent = () => {
        switch (selectedKey) {
            case '1':
                return <ContactFormSubmitted />;
            case '2':
                return <AdminContent />;
            case '3':
                return <RegistrantDashboard />;
            // case '4':
            //     return <SeatingContent />;
            case '5':
                return <SeatingEvent />;
            // case '6':
            //     return <GeneralSeat />;
            case '13':
                return <PastRegistrantDashboard />;
            case '14':
                return <SystemSettings />;
            case '15':
                return <OrchestraSettings />;
            case '17':
                return <VenueSettings />;
            case '18':
                return <TicketPricingSettings />;
            case '19':
                return <PerformerSessionsSettings />;
            case '16':
                return <PublicCustomersList />;
            case '20':
                return <JuryDeadlineSettings />;
            case '21':
                return <ScoringRecap />;
            case '22':
                return <SeatOccupancy />;
            case '25':
                return <OrchestraAssignments />;
            case '24':
                return <JuryManagement />;
            case '27':
                return <VideoPenaltySettings />;
            default:
                return <ContactFormSubmitted />;
        }
    };

    return (
        <Layout
            style={{
                minHeight: '100vh',
            }}
        >
            <Sider collapsible collapsed={collapsed} onCollapse={(value) => setCollapsed(value)}>
                <div className="demo-logo-vertical" />
                <Menu onClick={onMenuClick} theme="dark" mode="inline" items={items} selectedKeys={[selectedKey]} />
            </Sider>
            <Layout>
                <Header
                    style={{
                        padding: '0 24px',
                        background: colorBgContainer,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #f0f0f0',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                    }}
                >
                    <div style={{ fontWeight: 600, fontSize: '16px', color: '#1f1f1f' }}>
                        {selectedKey === '27'
                            ? t('videoPenalty.menuTitle')
                            : (MENU_TITLES[selectedKey] || 'APCS Admin Portal')}
                    </div>

                    <Space size="middle" align="center">
                        <Tooltip title="Session automatically extends as you work, or click here to refresh (60m limit)">
                            <Tag
                                icon={<ClockCircleOutlined />}
                                color={sessionRemainingMs <= (SESSION_WARNING_MS || 180000) ? 'error' : 'default'}
                                style={{
                                    cursor: 'pointer',
                                    padding: '4px 10px',
                                    borderRadius: '6px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    userSelect: 'none',
                                }}
                                onClick={extendSession}
                            >
                                <span>Session: {formatRemaining(sessionRemainingMs)}</span>
                                <SyncOutlined style={{ fontSize: '11px', color: '#1890ff' }} />
                            </Tag>
                        </Tooltip>

                        {loggedInUser && (
                            <Space size="small">
                                <Tag color="blue" style={{ textTransform: 'capitalize' }}>
                                    {loggedInUser.role || 'Admin'}
                                </Tag>
                                <span
                                    style={{
                                        fontSize: '13px',
                                        color: '#595959',
                                        maxWidth: '220px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'inline-block',
                                    }}
                                    title={loggedInUser.email}
                                >
                                    {loggedInUser.email}
                                </span>
                            </Space>
                        )}

                        <Button
                            type="text"
                            danger
                            icon={<LogoutOutlined />}
                            onClick={handleSignOut}
                            title="Sign out"
                        >
                            Sign out
                        </Button>
                    </Space>
                </Header>
                {ShowWhichContent()}
                <Footer
                    style={{
                        textAlign: 'center',
                    }}
                >
                    Ant Design ©{new Date().getFullYear()} Created by Ant UED
                </Footer>
            </Layout>

            <Modal
                title={
                    <Space>
                        <WarningOutlined style={{ color: '#faad14', fontSize: '18px' }} />
                        <span>Session Expiring Soon</span>
                    </Space>
                }
                open={isWarningOpen}
                closable={false}
                maskClosable={false}
                footer={[
                    <Button key="logout" danger onClick={handleSignOut}>
                        Sign Out
                    </Button>,
                    <Button key="stay" type="primary" onClick={extendSession}>
                        Stay Logged In
                    </Button>,
                ]}
            >
                <p>
                    You have been inactive. For your security, your session will automatically expire in{' '}
                    <strong style={{ color: '#cf1322' }}>
                        {Math.max(1, Math.ceil((sessionRemainingMs || 0) / 1000))} seconds
                    </strong>.
                </p>
                <p style={{ marginBottom: 0 }}>
                    Click <strong>Stay Logged In</strong> to continue your session without interruption.
                </p>
            </Modal>
        </Layout>
    )
}

export default AdminDashboard;
